import { Space, Tooltip } from 'antd';
import classnames from 'classnames/bind';
import { useMemo, useState } from 'react';
import { TimeOfUseTariffType, EPriceTime, TimeOfUseTariffTypeColor, PricedisplayType } from '@/shared/types';
import styles from './index.module.scss';
import { getItemTime, getPeriodTime, getPeriodTypes } from './utils';

const cx = classnames.bind(styles);

interface EPricePeakItemProps {
  value?: EPriceTime[];
  onChange?: (times: EPriceTime[]) => void;
  displayType?: PricedisplayType;
  disabled?: boolean;
  disabledIntervals?: EPriceTime[];
}
const timeList = ['00:00', '06:00', '12:00', '18:00', '次日00:00', '06:00', '12:00', '18:00', '00:00'];
const EPricePeakItem: React.FC<EPricePeakItemProps> = ({
  value = [],
  onChange,
  displayType,
  disabled = false,
  disabledIntervals = [],
}) => {
  const [ePricePeakType, setEPricePeakType] = useState<TimeOfUseTariffType>(3);
  const [range, setRange] = useState<[number | undefined, number | undefined]>([undefined, undefined]);
  const [start, end] = range;
  const isDisabledInterval = (index: number) => {
    return disabledIntervals.some(interval => index >= interval.intervalStart && index <= interval.intervalEnd);
  };

  const getItemColor = (index: number) => {
    if (isDisabledInterval(index)) {
      return 'rgba(255,255,255,0.2)'; // 灰色表示不可选择
    }
    const [tempStart, tempEnd] = [start, end].sort((a, b) => (a || 0) - (b || 0));
    if (tempStart !== undefined && index >= tempStart && tempEnd !== undefined && index <= tempEnd) {
      return `${TimeOfUseTariffTypeColor[ePricePeakType!]}`;
    }
    const time = value.find(time => time.intervalStart <= index && time.intervalEnd >= index);
    if (time) {
      return TimeOfUseTariffTypeColor[time.type];
    }
    return ' rgba(74,144,226,0.2)';
  };

  const count = useMemo(() => {
    if (displayType === undefined) return 0;
    return displayType === PricedisplayType.HALF ? 96 : 48;
  }, [displayType]);

  const onItemClick = (index: number) => {
    if (isDisabledInterval(index)) {
      return;
    }
    if (ePricePeakType === undefined) {
      return;
    }
    if (displayType === undefined) {
      return;
    }
    if (start === undefined) {
      setRange([index, index]);
      return;
    }

    const [tempStart, tempEnd] = [start, index].sort((a, b) => a - b);
    // 检查范围内的每个索引是否为禁用区间
    for (let i = tempStart; i <= tempEnd; i++) {
      if (isDisabledInterval(i)) {
        return;
      }
    }
    onChange?.(
      getPeriodTypes(
        [
          ...value,
          {
            type: ePricePeakType!,
            intervalStart: tempStart,
            intervalEnd: tempEnd,
          },
        ],
        displayType
      ).filter((time: EPriceTime) => time.type !== undefined)
    );

    setRange([undefined, undefined]);
  };

  const onItemMouseEnter = (index: number) => {
    if (isDisabledInterval(index)) {
      return;
    }
    if (ePricePeakType === undefined) {
      return;
    }
    if (start === undefined) {
      return;
    }
    setRange([start, index]);
  };

  const onItemMouseLeave = () => {
    setRange([undefined, undefined]);
  };

  return (
    <div style={{ display: 'inline-block' }}>
      {!disabled ? (
        <>
          <Space direction="vertical">
            <div className={cx('peak-item')} style={{ position: 'relative' }} onMouseLeave={() => onItemMouseLeave()}>
              {[...new Array(count)].map((_, index) => (
                <Tooltip
                  title={`${getItemTime(index, displayType!)}~${getItemTime(index + 1, displayType!)}`}
                  key={index}
                >
                  <span
                    className={cx(
                      'bar',
                      displayType === PricedisplayType.HALF ? 'half' : '',
                      displayType === PricedisplayType.HOUR ? 'hour' : ''
                    )}
                    // style={{ background: getItemColor(index) }}
                    onClick={() => !isDisabledInterval(index) && onItemClick(index)}
                    onMouseEnter={() => !isDisabledInterval(index) && onItemMouseEnter(index)}
                    style={{
                      background: getItemColor(index),
                      cursor: isDisabledInterval(index) ? 'not-allowed' : 'pointer',
                    }}
                  />
                </Tooltip>
              ))}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: -4,
                  bottom: 0,
                  width: 1,
                  height: 42,
                  backgroundColor: 'white',
                  transform: 'translateX(-50%)',
                }}
              />
            </div>
          </Space>
          <div>
            {
              <Space size={44} style={{ marginLeft: '-19px' }}>
                {timeList.map((time, index) => (
                  <span key={index} style={{ whiteSpace: 'nowrap' }}>
                    {time}
                  </span>
                ))}
              </Space>

              /* {Array.from({ length: 18 }).map((_, i) =>
              i % 2 ? (
                <span key={`line-${i}`} style={{ display: 'inline-block', width: ` 13px` }}></span>
              ) : (
                <span key={`line-${i}-empty`}>{timeList[Math.floor(i / 2)]}</span>
              )
            )} */
            }
          </div>
          {/* <Space>
            <InfoCircleFilled style={{ color: 'yellow', marginRight: '5px' }} />
            {`先选中分段（尖峰平谷)，再在时间轴上分别单击选择起止时间块，直到无灰色时间块方可。`}
          </Space> */}
        </>
      ) : (
        getPeriodTypes(value, displayType!).map((item, index) => {
          if (item.type) {
            return (
              <Tooltip title={getPeriodTime(item, displayType!)} key={index}>
                {[...new Array(item.intervalEnd - item.intervalStart + 1)].map((_, i) => (
                  <span
                    key={i}
                    className={cx(
                      'bar',
                      displayType === PricedisplayType.HALF ? 'half' : '',
                      displayType === PricedisplayType.HOUR ? 'hour' : ''
                    )}
                    style={{
                      background: TimeOfUseTariffTypeColor[item.type],
                    }}
                  />
                ))}
              </Tooltip>
            );
          }
          return [...new Array(item.intervalEnd - item.intervalStart + 1)].map((_, i) => (
            <span key={i} className={cx('bar')} style={{ background: '#4A90E2 ' }} />
          ));
        })
      )}
    </div>
  );
};

export default EPricePeakItem;
